/**
 * ChatGPT AI service for consultation assistance
 * Provides AI-powered suggestions based on consultation notes and patient history
 */

// ChatGPT API configuration - API key should be set via settings
const CHATGPT_BASE_URL = 'https://api.openai.com/v1'

export interface ChatGPTSuggestion {
  type: 'diagnosis' | 'treatment' | 'test' | 'referral' | 'medication' | 'followup'
  title: string
  description: string
  confidence?: 'high' | 'medium' | 'low'
}

export interface ConsultationContext {
  patientAge?: number
  patientGender?: string
  reasonForVisit?: string
  notes?: string
  currentMedications?: string[]
  allergies?: string[]
  chronicConditions?: string[]
  medicalHistory?: string
  diagnosis?: string[]
  vitalSigns?: Record<string, any>
}

export const chatgptService = {
  /**
   * Get AI suggestions based on consultation context
   */
  getSuggestions: async (
    context: ConsultationContext,
    apiKey: string,
    model: string = 'gpt-4o-mini'
  ): Promise<ChatGPTSuggestion[]> => {
    try {
      if (!apiKey) {
        throw new Error('ChatGPT API key is required')
      }

      const prompt = buildPrompt(context)

      const response = await fetch(`${CHATGPT_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: model,
          messages: [
            {
              role: 'system',
              content:
                'You are a medical AI assistant helping a healthcare provider during a patient consultation. Provide clinical suggestions in a structured JSON format.',
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: 0.7,
          response_format: { type: 'json_object' },
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error?.message || `API error: ${response.statusText}`
        console.error('[ChatGPTService] API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          model,
        })
        throw new Error(errorMessage)
      }

      const data = await response.json()
      const generatedText = data.choices?.[0]?.message?.content || ''

      if (!generatedText) {
        console.warn('[ChatGPTService] No text in response:', data)
        return []
      }

      // Parse the AI response into structured suggestions
      return parseSuggestions(generatedText)
    } catch (error: any) {
      console.error('[ChatGPTService] Error getting suggestions:', error)
      // If it's a CORS or network error, provide a helpful message
      if (error.message?.includes('Failed to fetch') || error.message?.includes('CORS')) {
        throw new Error('Unable to connect to ChatGPT service. Please check your network connection.')
      }
      if (error.message?.includes('API key')) {
        throw new Error('Invalid ChatGPT API key. Please check your settings.')
      }
      throw new Error(`Failed to get AI suggestions: ${error.message || 'Unknown error'}`)
    }
  },

  /**
   * List available models (for reference)
   */
  getAvailableModels: (): string[] => {
    return [
      'gpt-4o',
      'gpt-4o-mini',
      'gpt-4-turbo',
      'gpt-4',
      'gpt-3.5-turbo',
    ]
  },
}

/**
 * Build a prompt for ChatGPT based on consultation context
 * NOTE: This context should NEVER contain PII (names, addresses, phone numbers, emails, patient IDs, etc.)
 * Only medical information is included for privacy compliance.
 */
function buildPrompt(context: ConsultationContext): string {
  const parts: string[] = []

  parts.push('Based on the following anonymized medical information (no patient identifiers included), provide clinical suggestions in a structured format.')
  parts.push('')

  if (context.patientAge && context.patientGender) {
    parts.push(`Patient: ${context.patientAge}-year-old ${context.patientGender}`)
  }

  if (context.reasonForVisit) {
    parts.push(`Reason for visit: ${context.reasonForVisit}`)
  }

  if (context.notes) {
    parts.push(`Clinical notes: ${context.notes}`)
  }

  if (context.currentMedications && context.currentMedications.length > 0) {
    parts.push(`Current medications: ${context.currentMedications.join(', ')}`)
  }

  if (context.allergies && context.allergies.length > 0) {
    parts.push(`Allergies: ${context.allergies.join(', ')}`)
  }

  if (context.chronicConditions && context.chronicConditions.length > 0) {
    parts.push(`Chronic conditions: ${context.chronicConditions.join(', ')}`)
  }

  if (context.medicalHistory) {
    parts.push(`Medical history: ${context.medicalHistory}`)
  }

  if (context.diagnosis && context.diagnosis.length > 0) {
    parts.push(`Current diagnosis: ${context.diagnosis.join(', ')}`)
  }

  parts.push('')
  parts.push('Provide suggestions in the following JSON format:')
  parts.push('{')
  parts.push('  "suggestions": [')
  parts.push('    {')
  parts.push('      "type": "diagnosis|treatment|test|referral|medication|followup",')
  parts.push('      "title": "Brief title",')
  parts.push('      "description": "Detailed description",')
  parts.push('      "confidence": "high|medium|low"')
  parts.push('    }')
  parts.push('  ]')
  parts.push('}')
  parts.push('')
  parts.push('Focus on clinically relevant suggestions. Return only valid JSON, no additional text.')

  return parts.join('\n')
}

/**
 * Parse AI response into structured suggestions
 */
function parseSuggestions(text: string): ChatGPTSuggestion[] {
  try {
    // Try to extract JSON from the response
    const jsonMatch = text.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0])
      // Handle both { suggestions: [...] } and direct array formats
      const suggestions = parsed.suggestions || parsed
      if (Array.isArray(suggestions)) {
        return suggestions.filter((s: any) => s.type && s.title && s.description)
      }
    }

    // Fallback: try to parse the entire response as JSON
    const parsed = JSON.parse(text)
    const suggestions = parsed.suggestions || parsed
    if (Array.isArray(suggestions)) {
      return suggestions.filter((s: any) => s.type && s.title && s.description)
    }

    return []
  } catch (error) {
    console.error('[ChatGPTService] Failed to parse suggestions:', error)
    // Return empty array if parsing fails
    return []
  }
}






























